import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { getUniqueBoroughs, toBoroughSlug, getPracticesByBorough } from "@/lib/boroughUtils";
import { useMemo, useState } from "react";

interface BoroughLinksProps {
  variant?: "grid" | "compact";
  limit?: number;
}

const BoroughLinks = ({ variant = "grid", limit }: BoroughLinksProps) => {
  const [showAll, setShowAll] = useState(false);

  const boroughs = useMemo(() => {
    const all = getUniqueBoroughs().map((b) => ({
      name: b,
      slug: toBoroughSlug(b),
      count: getPracticesByBorough(b).length,
    }));
    // Sort by count descending for relevance
    return all.sort((a, b) => b.count - a.count);
  }, []);

  const displayed = limit && !showAll ? boroughs.slice(0, limit) : boroughs;

  if (variant === "compact") {
    return (
      <div className="space-y-1.5">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <MapPin className="h-3.5 w-3.5 text-primary" />
          Browse by Area
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {displayed.map((b) => (
            <Link
              key={b.slug}
              to={`/area/${b.slug}`}
              className="rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              {b.name}
              <span className="ml-1 opacity-60">({b.count})</span>
            </Link>
          ))}
        </div>
        {limit && !showAll && boroughs.length > limit && (
          <button
            onClick={() => setShowAll(true)}
            className="text-xs font-medium text-primary hover:underline"
          >
            Show all {boroughs.length} areas
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-center text-2xl font-bold text-foreground sm:text-3xl">
        Browse by Area
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-center text-sm text-muted-foreground">
        Find practices in your local area
      </p>
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {displayed.map((b) => (
          <Link
            key={b.slug}
            to={`/area/${b.slug}`}
            className="group flex items-center justify-between rounded-lg border border-border/60 bg-card px-3 py-2.5 transition-colors hover:border-primary/30 hover:bg-secondary"
          >
            <span className="text-sm font-medium text-foreground group-hover:text-primary">
              {b.name}
            </span>
            <span className="text-xs text-muted-foreground">{b.count}</span>
          </Link>
        ))}
      </div>
      {limit && !showAll && boroughs.length > limit && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(true)}
            className="text-sm font-medium text-primary hover:underline"
          >
            Show all {boroughs.length} areas →
          </button>
        </div>
      )}
    </div>
  );
};

export default BoroughLinks;
